<template>
  <span
    v-if="!disabled && (text || $slots.content)"
    role="tooltip"
    ref="tooltip"
    class="leading-normal ds-tooltip"
    :class="tooltipClasses"
    :data-microtip-position="fixedPosition || position"
    :tabindex="isClickable ? 0 : -1"
    aria-label="Display additional information"
    @click="clickHandler"
  >
    <div
      ref="tooltipContent"
      class="ds-tooltip__content"
      :class="{ [this.additionalContentClass]: this.additionalContentClass.length }"
    >
      <div v-if="header || showHideIcon" class="flex justify-between font-bold mb-2 text-sm">
        <div v-if="header" v-html="header" />
        <div v-if="showHideIcon" class="h-3 w-3 block cursor-pointer" @click.stop="hide()">
          <CancelIcon class="w-4 h-4" />
        </div>
      </div>
      <div v-html="text" />
      <slot name="content" />
    </div>
    <slot />
  </span>
  <slot v-else />
</template>

<script>
import CancelIcon from '@tagvenue/icons/24/bold/general/x-close.svg';

export const TOOLTIP_POSITION = {
  TOP_LEFT: 'top-left',
  TOP: 'top',
  TOP_RIGHT: 'top-right',
  LEFT: 'left',
  RIGHT: 'right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM: 'bottom',
  BOTTOM_RIGHT: 'bottom-right',
};

export default {
  name: 'Tooltip',
  components: {
    CancelIcon,
  },
  props: {
    header: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      validator: (position) => Object.values(TOOLTIP_POSITION).includes(position),
      default: TOOLTIP_POSITION.TOP,
    },
    mobilePosition: {
      type: String,
      validator: (position) => Object.values(TOOLTIP_POSITION).includes(position),
      required: false,
    },
    trigger: {
      type: String,
      default: 'hover',
      validator: (trigger) => ['hover', 'click', 'init'].includes(trigger),
    },
    theme: {
      type: String,
      default: 'dark',
      validator: (theme) => ['dark', 'light', 'warning'].includes(theme),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showHideIcon: {
      type: Boolean,
      default: false,
    },
    additionalContentClass: {
      type: String,
      default: '',
    },
    hideImmediately: {
      type: Boolean,
      default: false,
    },
    oneLine: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fixedPosition: null,
      show: false,
      hideTimeout: null,
      screenPadding: 10,
      decreasedZIndex: false,
    };
  },
  computed: {
    isClickable() {
      return this.trigger === 'click';
    },
    tooltipClasses() {
      return {
        'ds-tooltip--active': this.show,
        'ds-tooltip--light': this.theme === 'light',
        'ds-tooltip--warning': this.theme === 'warning',
        'ds-tooltip--clickable': this.trigger === 'click',
        'ds-tooltip--decreased-z-index': this.decreasedZIndex,
        'ds-tooltip--one-line': this.oneLine,
      };
    },
  },
  watch: {
    show(shouldShow) {
      if (shouldShow) {
        this.fixPosition();

        this.$emit('show');
      }
    },
    text() {
      this.$nextTick(() => {
        this.removeEventListeners();
        this.addEventListeners();
      });
    },
  },
  methods: {
    getTooltipTopPosition() {
      const slotPositionFromTop = this.$refs.tooltipContent.getBoundingClientRect().top;
      return slotPositionFromTop - this.screenPadding;
    },

    getTooltipBottomPosition() {
      const slotPositionFromBottom = window.screen.height - this.$refs.tooltipContent.getBoundingClientRect().bottom;
      return slotPositionFromBottom - this.screenPadding;
    },
    fixPosition() {
      if (!this.$refs.tooltipContent) {
        return;
      }
      const tooltipTopPosition = this.getTooltipTopPosition();
      const tooltipBottomPosition = this.getTooltipBottomPosition();

      if (!tooltipTopPosition || !tooltipBottomPosition) {
        return;
      }

      if (this.mobilePosition && window.innerWidth < 768) {
        this.fixedPosition = this.mobilePosition;
        return;
      }

      if (tooltipTopPosition < 0) {
        switch (this.position) {
          case TOOLTIP_POSITION.TOP_LEFT:
          case TOOLTIP_POSITION.LEFT:
            this.fixedPosition = TOOLTIP_POSITION.BOTTOM_LEFT;
            return;
          case TOOLTIP_POSITION.TOP:
            this.fixedPosition = TOOLTIP_POSITION.BOTTOM;
            return;
          case TOOLTIP_POSITION.TOP_RIGHT:
          case TOOLTIP_POSITION.RIGHT:
            this.fixedPosition = TOOLTIP_POSITION.BOTTOM_RIGHT;
            return;
          default:
        }
      }

      if (tooltipBottomPosition < 0) {
        switch (this.position) {
          case TOOLTIP_POSITION.BOTTOM_LEFT:
          case TOOLTIP_POSITION.LEFT:
            this.fixedPosition = TOOLTIP_POSITION.TOP;
            return;
          case TOOLTIP_POSITION.BOTTOM:
            this.fixedPosition = TOOLTIP_POSITION.TOP;
            return;
          case TOOLTIP_POSITION.BOTTOM_RIGHT:
          case TOOLTIP_POSITION.RIGHT:
            this.fixedPosition = TOOLTIP_POSITION.TOP_RIGHT;
            break;
          default:
        }
      }

      this.fixedPosition = this.position;
    },
    showHandler() {
      this.decreasedZIndex = false;
      this.show = true;
    },
    hideHandler() {
      this.decreasedZIndex = true;
      if (this.hideImmediately) {
        this.hide();
        return;
      }
      this.hideTimeout = setTimeout(() => {
        this.hide();
      }, 300);
    },
    hide() {
      this.show = false;
    },
    clickOutsideHandler(event) {
      if (this.$refs.tooltip && !this.$refs.tooltip.contains(event.target) && this.isClickable) {
        this.hide();
      }
    },
    clickHandler() {
      this.show = true;
    },

    addEventListeners() {
      if (!this.$refs.tooltip) {
        return;
      }
      this.$refs.tooltip.addEventListener('mouseenter', this.showHandler);

      if (this.trigger !== 'init') {
        this.$refs.tooltip.addEventListener('mouseleave', this.hideHandler);
      }
    },

    removeEventListeners() {
      if (!this.$refs.tooltip) {
        return;
      }
      this.$refs.tooltip.removeEventListener('mouseenter', this.showHandler);

      if (this.trigger !== 'init') {
        this.$refs.tooltip.removeEventListener('mouseleave', this.hideHandler);
      }
    },
  },

  mounted() {
    this.fixPosition();
    if (this.isClickable) {
      document.addEventListener('mousedown', this.clickOutsideHandler);
      return;
    }
    this.addEventListeners();

    if (this.trigger === 'init') {
      this.showHandler();
    }
  },

  beforeUnmount() {
    if (this.isClickable) {
      document.removeEventListener('mousedown', this.clickOutsideHandler);
      return;
    }
    this.removeEventListeners();
  },
};
</script>
